import { Badge, Icon, Input, Spinner, Text } from '@ui/themed';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { useDebouncedState } from '../../../hooks/use-debounced-state';
import { CUSTOMER_ROLE_TYPE } from '../../customers/constants';
import { useSearchCustomers } from '../../customers/hooks/use-search-customers';
import type { CustomerSearchQuery } from '../../customers/types/api';
import type { Customer } from '../../customers/types/customer';
import type { CustomerMinimal } from '../types/company';

const MIN_SEARCH_LENGTH = 2;

function customerLabel(customer: {
  first_name?: string;
  last_name?: string;
  email?: string;
}): string {
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || customer.email || '';
}

type CommonProps = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: string;
  onChange: (id: string) => void;
  /** Pre-selected customer so its label shows without a lookup (edit mode). */
  seed?: CustomerMinimal | null;
};

type MultiProps = CommonProps & {
  multiple: true;
  value: string[];
  onChange: (ids: string[]) => void;
  seed?: CustomerMinimal[];
};

type CustomerSearchSelectProps = SingleProps | MultiProps;

/**
 * A searchable customer picker (single or multi) backed by the customer search API.
 * Stock `Combobox` only filters a fixed client-side list, so we use the established
 * debounced-search pattern here to query the server as the user types.
 */
export function CustomerSearchSelect(props: CustomerSearchSelectProps) {
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedState(term, 300);

  // Remembers labels for ids the user has picked (and any seeds) so selected
  // chips render even when the customer isn't in the current search results.
  const [labels, setLabels] = useState<Record<string, string>>(() => {
    const seeded: Record<string, string> = {};
    if (props.multiple) {
      for (const customer of props.seed ?? []) {
        seeded[customer.id] = customerLabel(customer);
      }
    } else if (props.seed) {
      seeded[props.seed.id] = customerLabel(props.seed);
    }
    return seeded;
  });

  const query = useMemo<CustomerSearchQuery>(
    () =>
      debouncedTerm.length >= MIN_SEARCH_LENGTH
        ? { role_type: CUSTOMER_ROLE_TYPE.CUSTOMER, search_term: debouncedTerm }
        : { role_type: CUSTOMER_ROLE_TYPE.CUSTOMER },
    [debouncedTerm],
  );

  const { data: results, isFetching } = useSearchCustomers(query);

  const rememberLabel = (customer: Customer) => {
    const label = customerLabel(customer);
    setLabels((prev) => ({ ...prev, [customer.id]: label }));
    return label;
  };

  const select = (customer: Customer) => {
    rememberLabel(customer);
    if (props.multiple) {
      if (!props.value.includes(customer.id)) {
        props.onChange([...props.value, customer.id]);
      }
    } else {
      props.onChange(customer.id);
    }
    setTerm('');
  };

  const removeSingle = () => {
    if (!props.multiple) {
      props.onChange('');
    }
  };

  const removeMulti = (id: string) => {
    if (props.multiple) {
      props.onChange(props.value.filter((value) => value !== id));
    }
  };

  const showResults = debouncedTerm.length >= MIN_SEARCH_LENGTH;
  const availableResults = props.multiple
    ? (results ?? []).filter((customer) => !props.value.includes(customer.id))
    : (results ?? []);

  return (
    <div className="flex flex-col gap-1.5 text-sm text-spark-text-primary">
      {props.label && (
        <Text>
          {props.label}
          {props.required && <span className="text-spark-text-danger"> *</span>}
        </Text>
      )}

      {!props.multiple && props.value && (
        <div className="flex items-center justify-between gap-1.5 rounded-md border border-gray-300 bg-gray-100 px-2 py-1">
          <Text truncate>{labels[props.value] ?? props.value}</Text>
          <button
            type="button"
            aria-label={t('aria:remove_option', { option: labels[props.value] ?? props.value })}
            onClick={removeSingle}
            className="text-spark-text-secondary"
          >
            <Icon name="x" size="xs" />
          </button>
        </div>
      )}

      {props.multiple && props.value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {props.value.map((id) => (
            <div
              key={id}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100 px-1.5"
            >
              <Text>{labels[id] ?? id}</Text>
              <button
                type="button"
                aria-label={t('aria:remove_option', { option: labels[id] ?? id })}
                onClick={() => removeMulti(id)}
                className="text-spark-text-secondary"
              >
                <Icon name="x" size="xs" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Input
        icon="search"
        value={term}
        onChange={setTerm}
        placeholder={
          props.placeholder ??
          t('features:companies.components.customer_search_select.search_placeholder')
        }
      />

      {showResults && (
        <div className="max-h-60 overflow-auto rounded-md border border-gray-300 bg-white py-1 shadow-xs">
          {isFetching && (
            <div className="flex items-center gap-2 px-2 py-1 text-spark-text-secondary">
              <Spinner />
            </div>
          )}
          {!isFetching && availableResults.length === 0 && (
            <div className="px-2 py-1 text-spark-text-secondary">
              {t('features:companies.components.customer_search_select.no_options')}
            </div>
          )}
          {!isFetching &&
            availableResults.map((customer) => (
              <button
                key={customer.id}
                type="button"
                onClick={() => select(customer)}
                className="flex w-full items-center gap-1.5 px-2 py-1 text-start hover:bg-gray-100"
              >
                <Text>{customerLabel(customer)}</Text>
                {customer.email && <Badge>{customer.email}</Badge>}
              </button>
            ))}
        </div>
      )}

      {props.error && (
        <span className="flex items-center gap-0.5 text-spark-text-danger">
          <Icon name="xCircle" />
          <p className="text-xs">{props.error}</p>
        </span>
      )}
    </div>
  );
}
